import type { AsyncBelongsTo } from '@warp-drive/legacy/model';
import MediaType from '@linc-technologies/linced-common/enums/media-type';
import MediaSource from '@linc-technologies/linced-common/enums/media-source';
import type Media from '@linc-technologies/linced-common/models/media';
import { createBlobUrlCache } from '@linc-technologies/linced-common/utils/blob-url-lru-cache';
import type MediaService from '@linc-technologies/linced-common/services/media';

// A saved media record: this util uses its id as a cache key.
type SavedMedia = Media & { id: string };

// MediaUrlRecord is the slice of a media record this util reads.
export type MediaUrlRecord = Pick<
  SavedMedia,
  'id' | 'mimeType' | 'mediaType' | 'mediaSource' | 'rawUrl'
>;

// A PromiseBelongsTo/PromiseObject proxy, whose `content` is undefined until
// it settles.
type MediaUrlProxy = AsyncBelongsTo<SavedMedia>;

// A full record, a proxy around one, or a bare mediaId.
export type MediaUrlSource =
  string | MediaUrlRecord | MediaUrlProxy | null | undefined;

// LRU blob URL cache, keyed `${media.id}|pdf` or `${media.id}|<rendition>`
// for a full record, `${mediaId}|<rendition>` for a bare mediaId.
export const mediaUrlBlobUrlCache = createBlobUrlCache();

// Shared branching logic for resolveMediaUrl/peekMediaUrl, so they stay in
// lockstep on what's blobbable and its cache key. Returns null for a
// non-blobbable media (caller falls back to rawUrl). `media` must already be
// the real record, not a PromiseBelongsTo/PromiseObject proxy.
function mediaUrlCacheBranch(media: MediaUrlRecord, rendition: string) {
  const isInternalPdf =
    media.mimeType === 'application/pdf' &&
    media.mediaSource === MediaSource.INTERNAL;
  const isBlobbableImage =
    media.mediaType === MediaType.IMAGE &&
    media.mediaSource !== MediaSource.GOOGLE_DRIVE;

  if (!isInternalPdf && !isBlobbableImage) return null;

  return {
    isInternalPdf,
    cacheKey: isInternalPdf ? `${media.id}|pdf` : `${media.id}|${rendition}`,
  };
}

// Synchronously returns a URL for `media` if already known (a non-blobbable
// type's rawUrl, or an already-cached blob), without kicking off a fetch.
// Returns undefined when resolution needs the async path instead (cold
// cache, an unsettled `media` proxy, or an uncached bare mediaId).
//
// `media` is either a full media record (or a PromiseBelongsTo/PromiseObject
// proxy around one) or a bare mediaId string (badges, event images).
export function peekMediaUrl(
  media: MediaUrlSource,
  rendition = 'original',
): string | null | undefined {
  if (!media) return null;

  // Bare mediaId: no record to inspect, just peek the same cache key
  // resolveMediaUrl would resolve into.
  if (typeof media === 'string') {
    return mediaUrlBlobUrlCache.get(`${media}|${rendition}`) ?? undefined;
  }

  // A not-yet-settled PromiseBelongsTo/PromiseObject only safely exposes
  // `content` before it settles -- unwrap to the real record, or bail to the
  // async path if it hasn't landed yet.
  let record: MediaUrlRecord;
  if (typeof (media as Partial<MediaUrlProxy>).then === 'function') {
    const content = (media as MediaUrlProxy).content;
    if (content === undefined) return undefined;
    if (!content) return null;
    record = content;
  } else {
    record = media as MediaUrlRecord;
  }

  const branch = mediaUrlCacheBranch(record, rendition);
  if (!branch) return record.rawUrl;

  // The cache returns null for a miss, so normalise to undefined -- callers
  // rely on undefined specifically to mean "no synchronous answer".
  return mediaUrlBlobUrlCache.get(branch.cacheKey) ?? undefined;
}

// Resolves the best displayable URL for `media`.
//
// A bare mediaId resolves directly via the media service (like avatar-url
// does for a resource's mediaId): no record to inspect, so no rawUrl/PDF
// branching, and a miss returns null with no fallback.
//
// A full media record fetches internal PDFs and blobbable images as object
// URLs, falling back to the record's rawUrl for everything else (and on a
// failed fetch).
//
// rendition picks the image transform: 'original' (default) omits the query
// param; anything else (e.g. 'avatar') is passed through. Ignored for PDFs.
//
// mediaService is the `media` service, injected by the caller (a helper) so
// this util stays a plain, directly-testable function.
export async function resolveMediaUrl(
  mediaService: MediaService,
  media: MediaUrlSource,
  rendition = 'original',
): Promise<string | null> {
  if (typeof media === 'string') {
    const cacheKey = `${media}|${rendition}`;
    const cached = mediaUrlBlobUrlCache.get(cacheKey);
    if (cached) return cached;

    const url = await mediaService.getImageBlobUri(
      media,
      '',
      rendition === 'original' ? null : rendition,
    );
    if (url) mediaUrlBlobUrlCache.set(cacheKey, url);
    return url ?? null;
  }

  // Unwrap PromiseObject / PromiseBelongsTo proxy.
  const record = await media;
  if (!record) return null;

  const branch = mediaUrlCacheBranch(record, rendition);
  if (!branch) return record.rawUrl;

  const cached = mediaUrlBlobUrlCache.get(branch.cacheKey);
  if (cached) return cached;

  const url = branch.isInternalPdf
    ? await mediaService.getFileBlobUri(record.id)
    : await mediaService.getImageBlobUri(
        record.id,
        '',
        rendition === 'original' ? null : rendition,
      );

  if (url) {
    mediaUrlBlobUrlCache.set(branch.cacheKey, url);
    return url;
  }

  // Blob fetch returned nothing -- fall back to the record's rawUrl.
  return record.rawUrl;
}
